
import React from "react";
import { Schemes } from "../../../../../utils";
import { i18nManager } from "../../../../../i18n";
import { Elements } from "../../../../shared/Elements";
import * as ReactRouterDom from "react-router-dom";
import { BaseService, HostEarningReceiver, IHostManager, IMember, IHost, SanitizedResponse, HostRole, HostService } from "../../../../../services";
import { Button, ButtonManageRef, Checkbox, Dropdown, Form, FormControl, Scheme, TextInput } from "@rivtn/noseur";
import { Siren } from "../../../../shared/ConfirmDialog";

export interface HostEarningsProps {
    host: IHost;
    profile: IMember;
}

function HostEarnings(props: HostEarningsProps) {

    const { host } = props;
    const hostService = HostService.getInstance();
    const [profile, _] = React.useState<IMember>(props.profile);
    const [isHostOwner, setIsHostOwner] = React.useState<boolean>(false);
    const labels = i18nManager.Labels.host.settings.general.host_earnings;
    const [hostManagers, setHostManagers] = React.useState<IHostManager[]>();
    const receivingGroupOptions = [
        {
            value: HostEarningReceiver.OWNER,
            label: labels.receivers.owner_you,
        },
        {
            label: labels.receivers.selected_managers,
            value: HostEarningReceiver.SELECTED_HOST_MANAGERS,
        },
        {
            label: labels.receivers.split_equally_bet_mans,
            value: HostEarningReceiver.SPLIT_EQUALLY_BETWEEN_HOST_MANAGERS,
        },
    ];

    React.useEffect(fetchInformation, []);

    return (<div style={{ marginTop: 10, display: "flex", flexDirection: "column" }}>
        <span>{labels.info.part_1}</span>
        <ul>
            <li>{labels.info.part_2}</li>
            <li>{labels.info.part_3}</li>
            <li>{labels.info.part_4}</li>
        </ul>
        <span>{labels.info.part_5}</span>
        <Form scheme={Schemes.OMNEHOST} style={{ marginTop: 10 }}>
            <FormControl isFieldset fill scheme={Schemes.OMNEHOST} style={{ marginTop: 15 }} label={labels.who_receives}>
                <Dropdown fill disabled={!isHostOwner} selectedOptionIndex={receivingGroupOptions.findIndex((o) => o.value === host.earning_settings.receiving_group)} options={receivingGroupOptions}
                    popoverFooterTemplate={() => (<div style={{ display: "flex", flexDirection: "column", borderTop: "1px solid var(--underlineColor, #E3E3E3)", paddingTop: 10, paddingBottom: 10 }}>
                        {hostManagers?.map((hostManager) => {
                            return (<div key={hostManager.external_id} style={{ display: "flex", margin: 15, marginTop: 10 }}>
                                <Checkbox scheme={Schemes.OMNEHOST} disabled={host.earning_settings.receiving_group != HostEarningReceiver.SELECTED_HOST_MANAGERS}
                                    defaultChecked={host.earning_settings.receiving_member_ids?.includes(hostManager?.external_id ?? "")} onChecked={(v) => {
                                        const entryIndex = host.earning_settings.receiving_member_ids.indexOf(hostManager.external_id);
                                        if (entryIndex < 0) {
                                            host.earning_settings.receiving_member_ids.push(hostManager.external_id);
                                        } else {
                                            host.earning_settings.receiving_member_ids.splice(entryIndex, 1);
                                        }
                                    }} />
                                <div style={{ display: "flex", marginLeft: 10 }}>
                                    <img alt={hostManager.member?.username} src={hostManager.member?.profile_picture_url} style={{ width: 40, height: 40, borderRadius: "100%" }} />
                                    <div style={{ display: "flex", flexDirection: "column", marginLeft: 10, alignItems: "center", justifyContent: "center" }}>
                                        <span style={{ fontWeight: "bold" }}>{hostManager.member?.username}</span>
                                        <span>{hostManager.member?.user_detail?.email}</span>
                                    </div>
                                </div>
                            </div>);
                        })}
                    </div>)} onSelectOption={(o) => {
                        host.earning_settings.receiving_group = o.value;
                        if (o.value === HostEarningReceiver.OWNER) {
                            host.earning_settings.receiving_member_ids = [hostManagers?.find((h) => h.role === HostRole.OWNER)!.external_id!];
                        } else if (o.value === HostEarningReceiver.SPLIT_EQUALLY_BETWEEN_HOST_MANAGERS) {
                            host.earning_settings.receiving_member_ids = hostManagers?.map((h) => h.external_id ?? "") ?? [];
                        }
                    }} />
            </FormControl>
            <Checkbox disabled={!isHostOwner} defaultChecked={host.earning_settings.accepted_terms} style={{ marginTop: 20 }} onChecked={(v) => { host.earning_settings.accepted_terms = v; }}
                label={<span style={{ color: "var(--textColor)" }}>
                    {labels.accept_policy.accept}{" "}
                    <a target="_blank" href={labels.accept_policy.omne_policy.location}>{labels.accept_policy.omne_policy.label}</a>
                </span>} />
        </Form>
        <Button scheme={Schemes.OMNEHOST} loadingProps={Elements.BUTTON_LOADING_PROPS} text={i18nManager.Labels.common.save_changes}
            disabled={!isHostOwner} style={{ marginTop: 30, padding: "10px 25px" }} onClick={(e) => {
                updateHostEarning(host.earning_settings, e.manageRef);
            }} />
    </div>);

    function fetchInformation() {
        hostService.queryHostManagers(host.external_id, { status: "ACTIVE" }).then(({ content }: SanitizedResponse<IHostManager>) => {
            setHostManagers(content);
            for (const manager of content) {
                if (manager.role === HostRole.OWNER && manager.member?.external_id === profile.external_id) {
                    setIsHostOwner(true);
                    break;
                }
            }
        }).catch(BaseService.reportError);
    }

    function updateHostEarning(payload: any, manageRef?: ButtonManageRef) {
        manageRef?.setLoadingState(true);
        hostService.updateHostEarnings(host.external_id, payload).then(({ sanitized }: SanitizedResponse<any>) => {
            Siren.alert(labels.update_success);
        }).catch(BaseService.reportError).finally(() => {
            manageRef?.setLoadingState(false);
        });
    }

}

export default HostEarnings;
